import { useEffect, useRef, useState } from "react";
import { Box, Button, Flex, Tabs, Tooltip, useBreakpointValue, useDisclosure, useToast } from "@chakra-ui/react";
import { TabStepsList, TabStepsPanels } from "./components/Steps";
import { t } from "i18next";
import { ConfirmationDialog } from "../../../../components/ConfirmationDialog";
import * as React from "react";
import {
  organizationQuestionnaireKeys,
  useDefaultOrganizationQuestionnaireConfig,
  useFinalizeQuestionnaire
} from "../../../../hooks/query";
import { showErrorToast, showSuccessToast } from "../../../../utils";
import { useQueryClient } from "react-query";

function CategoryQuestionnaire({
                                 defaultValues,
                                 handleTabChange,
                                 returnToListRequested,
                                 returnToList,
                                 declineReturnToListRequest,
                                 allowedToSubmit,
                                 isPreview,
                                 canFinalize = true,
                                 onHasData
                               }) {
  const [categoryTabIndex, setCategoryTabIndex] = useState(0);
  const [finalize, setFinalize] = useState(false);
  const { isOpen: isConfirmDialogOpen, onOpen: onConfirmDialogOpen, onClose: onConfirmDialogClose } = useDisclosure();
  const toast = useToast();
  const queryClient = useQueryClient();
  const saveBtnRef = useRef();

  // Scope 3 is always considered to have data if enabled — tracking individual categories is impractical
  useEffect(() => {
    if (onHasData && isPreview) onHasData(true);
  }, [onHasData, isPreview]);

  const categoriesTabsState = useBreakpointValue({ base: true, xl: false });
  const [categoryTabsCollapsed, setCategoryTabsCollapsed] = useState(false);

  useEffect(() => {
    setCategoryTabsCollapsed(categoriesTabsState);
  }, [categoriesTabsState]);

  useEffect(() => {
    if (allowedToSubmit === true && returnToListRequested === true) {
      saveBtnRef.current.click();
    }
  }, [allowedToSubmit, returnToListRequested]);

  const getActiveCategories = () => {
    const categories = defaultValues.configurations;
    const activeCategories = [];
    Object.keys(categories)
      .filter(q => categories[q] && q !== "directEmissionsShouldBeFilled" && q !== "indirectEmissionsShouldBeFilled")
      .map(cat => {
        const _q = cat.substring(0, cat.indexOf("ShouldBe"));
        return activeCategories.push(_q);
      });
    return activeCategories;
  };

  const handleTabsCollapse = (areCollapsed) => {
    setCategoryTabsCollapsed(areCollapsed);
  };

  const handleFinalizeConfirmation = () => {
    const currentForm = document.getElementById(`formStep${getActiveCategories()[categoryTabIndex]}`);
    if (currentForm) {
      currentForm.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
      handleDialogClose(true);
    }
  };

  const handleDialogClose = (confirmed) => {
    if (confirmed !== true) {
      setFinalize(false);
    }
    onConfirmDialogClose();
  };

  const { mutate: finalizeQuestionnaire } = useFinalizeQuestionnaire({
    ...useDefaultOrganizationQuestionnaireConfig(),
    onSuccess: () => {
      showSuccessToast(toast, t("organizationQuestionnaire.page.finalized"));
      queryClient.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
      returnToList();
    },
    onError: err => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
      setFinalize(false);
    }
  });

  return (
    <Box>
      <Tabs
        variant="unstyled"
        orientation="vertical"
        overflow="hidden"
        position="relative"
        isLazy
        isManual
        index={categoryTabIndex}
        maxBlockSize={{ base: "none", lg: "lg" }}
        marginInlineEnd="-4"
        minHeight={"600px"}
      >
        <TabStepsList
          currentIndex={categoryTabIndex}
          categories={getActiveCategories()}
          tabsCollapsed={categoryTabsCollapsed}
          setTabsCollapsed={handleTabsCollapse}
        />
        <TabStepsPanels
          defaultValues={defaultValues}
          onTabChange={setCategoryTabIndex}
          categories={getActiveCategories()}
          finalize={finalize}
          tabsCollapsed={categoryTabsCollapsed}
          finalizeQuestionnaire={finalizeQuestionnaire}
          onSectionChange={() => handleTabChange(prev => prev - 1)}
          setFinalize={setFinalize}
          returnToListRequested={returnToListRequested}
          returnToList={returnToList}
          declineReturnToListRequest={declineReturnToListRequest}
          isPreview={isPreview}
        />
      </Tabs>
      {!isPreview &&
        <Flex
          direction="row"
          justify="space-between"
          gap={4}
          mt={8}
          position="sticky"
          bottom="0"
          bg="white"
          borderTop="1px solid"
          borderColor="gray.200"
          zIndex="10"
          py={4}
        >
          <Button
            flex={1}
            form={`formStep${getActiveCategories()[categoryTabIndex]}`}
            bg="limeShade.100"
            color="pureBlack"
            _hover={{ bg: "limeShade.200" }}
            type="submit"
            ref={saveBtnRef}
          >
            {t("buttons.save")}
          </Button>
          <Tooltip label={canFinalize ? "" : "Add data, methodology, or a file before finalising"} placement="top" hasArrow>
            <Button
              flex={1}
              bg="lime"
              color="pureBlack"
              _hover={{ bg: "limeShade.300" }}
              form={`formStep${getActiveCategories()[categoryTabIndex]}`}
              isDisabled={!canFinalize}
              onClick={() => {
                setFinalize(true);
                onConfirmDialogOpen();
              }}
            >
              {t("buttons.finalize")}
            </Button>
          </Tooltip>
        </Flex>
      }
      <ConfirmationDialog
        isOpen={isConfirmDialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleFinalizeConfirmation}
        headerText={t("questionnaireFinalizationDialog.header")}
        confirmationText={t("questionnaireFinalizationDialog.question")}
      />
    </Box>
  );
}

export default CategoryQuestionnaire;
